// src/personalHabilitiesTests/PersonalHabilitiesDashboard.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Visibility as ViewIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Psychology as PsychologyIcon,
  VerifiedUser as VerifiedUserIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  PlayArrow as ProcessIcon,
  ContentCopy as CopyIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderIndicator from '../layouts/TestReminderIndicator';
import { NotificationsActive as ReminderNowIcon } from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import PersonalHabilitiesTestResultModal from './PersonalHabilitiesTestResultModal';
import { db } from '../config/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';

const filterByName = createFilterOptions({
  stringify: (option) => `${option?.name || ''}`,
  trim: true,
});

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reasoning-tabpanel-${index}`}
      aria-controls={`reasoning-tabpanel-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PersonalHabilitiesTestDashboard = () => {
  const { t } = useTranslation();
  const { authToken, project, organization, showAlert, user } = useStore();

  const [tabValue, setTabValue] = useState(0);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTest, setSelectedTest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [applicantFilter, setApplicantFilter] = useState(null);
  const [processing, setProcessing] = useState({});

  // Paginación de la tabla
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Volver a la primera página al cambiar de tab o de filtro
  useEffect(() => {
    setPage(0);
  }, [applicantFilter, tabValue]);

  // Estados de check de puntos
  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  const testTypes = [
    { value: 'all', label: t('reasoningTests.allTests'), icon: <PsychologyIcon /> },
    { value: 'integrityTest', label: t('personalHabilitiesTests.integrityTest'), icon: <VerifiedUserIcon /> },
    { value: 'emotionalIntelligence', label: t('personalHabilitiesTests.emotionalIntelligence'), icon: <FavoriteIcon /> },
  ];

  const fetchTests = async () => {
    setLoading(true);
    setError(null);

    if (!project) {
      setLoading(false);
      setAllTests([]);
      return;
    }

    try {
      const response = await api.get(
        '/reasoning-tests/personal-habilities-by-project',
        {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.errorCode === 0) {
        setAllTests(response.data.response.data || []);
      } else {
        setError(response.data.errorMessage);
      }
    } catch (err) {
      console.error('Error fetching tests:', err);
      setError(t('personalHabilitiesTests.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
  }, [project]);

  // Verificar límites de uso para procesar
  useEffect(() => {
    const checkLimits = async () => {
      if (!organization || !authToken) return;

      try {
        setLoadingUsageCheck(true);
        const { data } = await api.get(
          `/organizations/${organization}/usage/check`,
          {
            params: { action: 'reasoningTest' },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (data.response) {
          setUsageCheck(data.response);
        }
      } catch (error) {
        console.error('Error checking usage limits:', error);
      } finally {
        setLoadingUsageCheck(false);
      }
    };

    checkLimits();
  }, [organization, authToken]);

  // SUSCRIPCIÓN A FIRESTORE - Escuchar tests en processing
  useEffect(() => {
    if (!allTests || allTests.length === 0) return;

    const unsubscribers = [];

    allTests.forEach(test => {
      if (test.status === 'processing') {
        const testRef = doc(db, 'reasoning_tests', test.id);
        const unsubscribe = onSnapshot(testRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.status !== 'processing') {
              console.log(`✅ Test ${test.id} cambió de processing a ${data.status}`);
              fetchTests();
            }
          }
        });
        unsubscribers.push(unsubscribe);
      }
    });

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [allTests]);

  const getFilteredTests = () => {
    let filtered = allTests;

    if (tabValue !== 0) {
      const selectedType = testTypes[tabValue].value;
      filtered = filtered.filter(test => test.testType === selectedType);
    }

    if (applicantFilter) {
      filtered = filtered.filter(test => test.applicantId === applicantFilter.id);
    }

    return filtered;
  };

  const handleResendTest = async (testId) => {
    try {
      await api.post(
        `/reasoning-tests/${testId}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      showAlert(t('personalHabilitiesTests.testResentSuccess'), 'success');
      fetchTests();
    } catch (err) {
      console.error('Error resending test:', err);
      showAlert(t('personalHabilitiesTests.testResentError'), 'error');
    }
  };

  const handleProcessTest = async (testId) => {
    setProcessing(prev => ({ ...prev, [testId]: true }));

    try {
      const response = await api.post(
        `/reasoning-tests/${testId}/process?projectId=${project}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        showAlert(t('personalHabilitiesTests.processTestSuccess'), 'success');
        fetchTests();
      } else {
        showAlert(response.data.errorMessage || t('personalHabilitiesTests.processTestError'), 'error');
      }
    } catch (err) {
      console.error('Error processing test:', err);
      if (err.response?.status === 409 &&
        err.response?.data?.response?.status_response === 'LIMIT_EXCEEDED') {
        showAlert(t('reasoningTests.usageCheck.limitReached'), 'error');
        setUsageCheck(prev => prev ? { ...prev, canPerform: false } : prev);
      } else {
        showAlert(err.response?.data?.errorMessage || t('personalHabilitiesTests.processTestError'), 'error');
      }
    } finally {
      setProcessing(prev => ({ ...prev, [testId]: false }));
    }
  };

  const [sendingReminder, setSendingReminder] = useState({});
  // Manda el correo de recordatorio en el acto y reinicia el reloj: el proximo
  // recordatorio automatico se cuenta desde ahora. Distinto de "reenviar", que
  // vuelve a mandar el correo original del test.
  const handleSendReminderNow = async (test) => {
    setSendingReminder((prev) => ({ ...prev, [test.id]: true }));
    try {
      await api.post(
        `/reasoning-tests/${test.id}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      showAlert(t('testReminder.reminderSent'), 'success');
      fetchTests();
    } catch (err) {
      console.error('Error sending reminder:', err);
      showAlert(err.response?.data?.errorMessage || t('testReminder.reminderError'), 'error');
    } finally {
      setSendingReminder((prev) => ({ ...prev, [test.id]: false }));
    }
  };

  // Copiar el enlace es el momento en que el consultor lo envia por su cuenta,
  // asi que el test pasa de "pendiente" a "enviado" y queda habilitado el reenvio.
  // No agenda recordatorio: eso solo ocurre cuando el correo lo manda el sistema,
  // porque si no el recordatorio saldria de un remitente que el candidato no vio.
  const handleCopyTestUrl = async (test) => {
    const testUrl = `${window.location.origin}/reasoning-test/${test.id}`;

    try {
      await navigator.clipboard.writeText(testUrl);
    } catch (err) {
      console.error('Error copying URL:', err);
      showAlert(t('personalHabilitiesTests.urlCopyError'), 'error');
      return;
    }

    if (test.status !== 'pending') {
      showAlert(t('personalHabilitiesTests.urlCopied'), 'success');
      return;
    }

    try {
      await api.post(
        `/reasoning-tests/${test.id}/mark-as-sent`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      showAlert(t('testReminder.urlCopiedAndMarked'), 'success');
      fetchTests();
    } catch (err) {
      console.error('Error marking test as sent:', err);
      showAlert(t('personalHabilitiesTests.urlCopied'), 'success');
    }
  };

  const handleViewResult = (test) => {
    setSelectedTest(test);
    setModalOpen(true);
  };

  const handleOpenDeleteDialog = (test) => {
    setTestToDelete(test);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTestToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!testToDelete) return;

    setDeleting(true);
    try {
      const response = await api.delete(
        `/reasoning-tests/${testToDelete.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        showAlert(t('personalHabilitiesTests.testDeletedSuccess'), 'success');
        fetchTests();
      } else {
        showAlert(response.data.errorMessage || t('personalHabilitiesTests.testDeletedError'), 'error');
      }
    } catch (err) {
      console.error('Error deleting test:', err);
      showAlert(t('personalHabilitiesTests.testDeletedError'), 'error');
    } finally {
      setDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  const canDeleteTest = () => {
    return ['super-admin', 'admin', 'admin-grafton'].includes(user?.rol);
  };

  const getUniqueApplicants = () => {
    const applicantsMap = new Map();
    allTests.forEach(test => {
      if (test.applicantId && !applicantsMap.has(test.applicantId)) {
        applicantsMap.set(test.applicantId, {
          id: test.applicantId,
          name: test.applicantName,
          email: test.applicantEmail,
        });
      }
    });
    return Array.from(applicantsMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));
  };

  const uniqueApplicants = getUniqueApplicants();

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      sent: 'info',
      started: 'warning',
      completed: 'success',
      processing: 'warning',
      evaluated: 'success',
      processing_error: 'error',
      expired: 'error',
    };
    return colors[status] || 'default';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('personalHabilitiesTests.notAvailable');
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTestTypeLabel = (testType) => {
    return t(`personalHabilitiesTests.${testType}`);
  };

  const renderTestsTable = (tests) => {
    if (tests.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('personalHabilitiesTests.noTestsFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('personalHabilitiesTests.noTestsMessage')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper>
      <TableContainer sx={{ maxHeight: 560 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('personalHabilitiesTests.applicantName')}</TableCell>
              <TableCell>{t('personalHabilitiesTests.testType')}</TableCell>
              <TableCell>{t('personalHabilitiesTests.createdBy')}</TableCell>
              <TableCell>{t('personalHabilitiesTests.status')}</TableCell>
              <TableCell>{t('personalHabilitiesTests.sentDate')}</TableCell>
              <TableCell>{t('personalHabilitiesTests.completedDate')}</TableCell>
              <TableCell align="center">{t('personalHabilitiesTests.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((test) => (
              <TableRow key={test.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {test.applicantName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {test.applicantEmail}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getTestTypeLabel(test.testType)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {test.userEmail || t('personalHabilitiesTests.notAvailable')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`personalHabilitiesTests.status${test.status.charAt(0).toUpperCase() + test.status.slice(1)}`)}
                    color={getStatusColor(test.status)}
                    size="small"
                  />
                  <TestReminderIndicator test={test} />
                </TableCell>
                <TableCell>{formatDate(test.sentAt)}</TableCell>
                <TableCell>
                  {test.completedAt ? formatDate(test.completedAt) : '-'}
                </TableCell>
                <TableCell align="center">
                  {/* Botón PROCESAR - Solo si está completed */}
                  {test.status === 'completed' && (
                    <Tooltip title={usageCheck && !usageCheck.canPerform
                      ? t('reasoningTests.usageCheck.limitReached')
                      : t('personalHabilitiesTests.processTest')
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleProcessTest(test.id)}
                          disabled={processing[test.id] || (usageCheck && !usageCheck.canPerform)}
                          color="primary"
                        >
                          {processing[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ProcessIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón VER - Deshabilitado si está processing, habilitado si está evaluated */}
                  {(test.status === 'processing' || test.status === 'evaluated') && (
                    <Tooltip title={test.status === 'processing' ? t('personalHabilitiesTests.processing') : t('personalHabilitiesTests.viewResult')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleViewResult(test)}
                          disabled={test.status === 'processing'}
                          color="primary"
                        >
                          {test.status === 'processing' ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ViewIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón REPROCESAR - Solo para super-admin en tests evaluated */}
                  {test.status === 'evaluated' && user.rol === 'super-admin' && (
                    <Tooltip title={usageCheck && !usageCheck.canPerform
                      ? t('reasoningTests.usageCheck.limitReached')
                      : t('reprocess')
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleProcessTest(test.id)}
                          disabled={processing[test.id] || (usageCheck && !usageCheck.canPerform)}
                          color="warning"
                        >
                          {processing[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReplayIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón REINTENTAR - Solo si hay error */}
                  {test.status === 'processing_error' && (
                    <Tooltip title={usageCheck && !usageCheck.canPerform
                      ? t('reasoningTests.usageCheck.limitReached')
                      : (test.processingError || t('personalHabilitiesTests.errorDetails'))
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleProcessTest(test.id)}
                          disabled={processing[test.id] || (usageCheck && !usageCheck.canPerform)}
                          color="warning"
                        >
                          {processing[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <RefreshIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón COPIAR URL */}
                  <Tooltip title={t('personalHabilitiesTests.copyTestUrl')}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyTestUrl(test)}
                      color="info"
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Boton ENVIAR TEST: solo mientras el test no ha salido */}
                  {test.status === 'pending' && (
                    <Tooltip title={t('personalHabilitiesTests.sendTest')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleResendTest(test.id)}
                          color="secondary"
                        >
                          <SendIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Boton RECORDAR: envio inmediato del recordatorio */}
                  {test.status === 'sent' && (
                    <Tooltip title={t('testReminder.sendReminderNow')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleSendReminderNow(test)}
                          disabled={!!sendingReminder[test.id]}
                          color="primary"
                        >
                          {sendingReminder[test.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReminderNowIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón ELIMINAR */}
                  <Tooltip title={t('personalHabilitiesTests.deleteTest')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(test)}
                      color="error"
                      disabled={!canDeleteTest()}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={tests.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const filteredTests = getFilteredTests();

  return (
    <Box>
      {/* Alert de puntos insuficientes */}
      {!loadingUsageCheck && usageCheck && !usageCheck.canPerform && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            {usageCheck.reason === 'SERVICE_NOT_AVAILABLE'
              ? t('reasoningTests.usageCheck.serviceNotAvailable')
              : t('reasoningTests.usageCheck.limitReached')
            }
          </Typography>
          {usageCheck.message && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {usageCheck.message}
            </Typography>
          )}
          {usageCheck.remaining !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {t('reasoningTests.usageCheck.pointsAvailable', { points: usageCheck.remaining })}
            </Typography>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            {t('personalHabilitiesTests.dashboardTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('personalHabilitiesTests.dashboardSubTitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            options={uniqueApplicants}
            getOptionLabel={(option) => option?.name || ''}
            getOptionKey={(option) => option.id ?? option.email}
            isOptionEqualToValue={(option, value) =>
              (option.id ?? option.email) === (value.id ?? value.email)
            }
            filterOptions={filterByName}
            value={applicantFilter}
            onChange={(event, newValue) => setApplicantFilter(newValue)}
            sx={{ minWidth: 250 }}
            noOptionsText={t('noData')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('personalHabilitiesTests.searchApplicant')}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <PersonIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id ?? option.email}>
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </li>
            )}
          />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTests}
          >
            {t('disc.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Tabs por tipo de test */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {testTypes.map((type, index) => (
            <Tab
              key={type.value}
              icon={type.icon}
              label={type.label}
              id={`reasoning-tab-${index}`}
              aria-controls={`reasoning-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Contenido de cada tab */}
      {testTypes.map((type, index) => (
        <TabPanel key={type.value} value={tabValue} index={index}>
          {renderTestsTable(filteredTests)}
        </TabPanel>
      ))}

      {/* Modal para ver resultados */}
      <PersonalHabilitiesTestResultModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        test={selectedTest}
      />

      {/* Modal de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('personalHabilitiesTests.deleteTestTitle')}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('personalHabilitiesTests.deleteTestConfirm')} <strong>"{testToDelete?.applicantName}"</strong>?
          </Typography>
          {testToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('personalHabilitiesTests.applicantName')}:</strong> {testToDelete.applicantName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('personalHabilitiesTests.testType')}:</strong> {getTestTypeLabel(testToDelete.testType)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('personalHabilitiesTests.status')}:</strong> {t(`personalHabilitiesTests.status${testToDelete.status.charAt(0).toUpperCase() + testToDelete.status.slice(1)}`)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error">
            {t('noWayBack')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonalHabilitiesTestDashboard;